import { useMemo } from "react";
import { first, flatten, isNumber, isObject } from "lodash";
import type { UseInfiniteQueryOptions } from "react-query";
import { useInfiniteQuery } from "react-query";
import { t } from "@/i18n-js/instance";
import { marketingEmailTemplatesApi } from "@/react/api/marketingEmailTemplateApi";
import type { MarketingEmailTemplate } from "@/react/types/MarketingEmailTemplates";
import { useToast } from "@circle-react-uikit/ToastV2";

export const MARKETING_EMAIL_TEMPLATE_LIST_QUERY_KEY_PREFIX = [
  "marketing_email_templates",
  "list",
];

export const useTemplatesInfiniteQuery = ({
  params,
  initialPage = 1,
  queryOptions,
}: {
  initialPage?: number;
  queryOptions?: UseInfiniteQueryOptions;
} & Parameters<typeof marketingEmailTemplatesApi.list>[0]) => {
  const toastr = useToast();

  const result = useInfiniteQuery({
    queryKey: [...MARKETING_EMAIL_TEMPLATE_LIST_QUERY_KEY_PREFIX, params],
    queryFn: ({ pageParam = initialPage }: { pageParam?: number }) =>
      marketingEmailTemplatesApi.list({
        params: {
          ...params,
          page: pageParam,
        },
      }),
    onError: () => toastr.error(t("error")),
    getNextPageParam: (lastPage: any) =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
    getPreviousPageParam: (firstPage: any) =>
      firstPage.page > 1 ? firstPage.page - 1 : undefined,
    keepPreviousData: true,
    ...queryOptions,
  });

  const pages = useMemo(() => result.data?.pages || [], [result.data?.pages]);
  const templates = useMemo<MarketingEmailTemplate[]>(
    () => flatten(pages.map((page: any) => page.records)),
    [pages],
  );
  const totalCount = useMemo<number | null>(() => {
    const firstPage = first(pages);
    if (
      isObject(firstPage) &&
      "count" in firstPage &&
      isNumber(firstPage.count)
    ) {
      return firstPage.count;
    }
    return null;
  }, [pages]);

  return {
    ...result,
    templates,
    totalCount,
  };
};
